// Shared TypeScript types for the RFP tool

export interface LeaseRow {
    lease_name: string;
    avg_monthly_production_bbl: number;
    avg_bsw: number;
    avg_gravity: number;
    latitude: number;
    longitude: number;
    region?: string;
}

export interface BswBracket {
    bsw_low: number;
    bsw_high: number;
    discount: number;
    /** 'fixed' = flat $/bbl deduct; 'percent' = -(discount/100) × reference_price, matching disc_percentage=1 in get_market_price.py */
    deduct_type?: 'fixed' | 'percent';
}

export interface DestinationConfig {
    station_owner: string;
    station: string;
    market: string;
    base_deduct: number;
    bsw_schedule: BswBracket[];
    /** Provide for custom destinations not in market_coordinates */
    latitude?: number;
    longitude?: number;
    is_custom?: boolean;
    /** Sale-side: which components apply to this destination's pricing formula */
    component_flags: Record<string, boolean>;
    /** Sale-side: WTI/WTL gravity split enabled for this destination */
    wti_wtl_enabled: boolean;
    /** Sale-side: API gravity threshold for WTI/WTL split (default 44) */
    wti_wtl_gravity_threshold: number;
}

export interface PricingStrategyConfig {
    base_deduct: number;
    bsw_schedule: BswBracket[];
    component_flags: Record<string, boolean>;
    component_values: Record<string, number>;
    wti_wtl_enabled: boolean;
    wti_wtl_gravity_threshold: number;
}

export const COMPONENT_KEYS = [
    'cmat', 'cmaa', 'mid_cush_diff', 'cma_diff', 'wtl', 'wts',
    'hls', 'dapl_jc', 'argus_guernsey', 'argus_p', 'nymex_2_3_1_3', 'actual_roll',
    'plains_wti', 'meh',
] as const;

export type ComponentKey = typeof COMPONENT_KEYS[number];

export const COMPONENT_LABELS: Record<ComponentKey, string> = {
    cmat:           'CMAT',
    cmaa:           'CMAA',
    mid_cush_diff:  'Mid Cush Diff',
    cma_diff:       'CMA Diff',
    wtl:            'WTL',
    wts:            'WTS',
    hls:            'HLS',
    dapl_jc:        'DAPL JC',
    argus_guernsey: 'Argus Guernsey',
    argus_p:        'Argus P+',
    nymex_2_3_1_3:  'NYMEX 2/3-1/3',
    actual_roll:    'Actual Roll',
    plains_wti:     'Plains WTI',
    meh:            'MEH',
};

export interface RFPConfig {
    primary_destination: DestinationConfig;
    secondary_destination?: DestinationConfig;
    waste_destination?: DestinationConfig;
    bsw_threshold: number;
    carrier: string;
}

export interface MonteCarloConfig {
    enabled: boolean;
    /** Number of simulations per lease (default 300) */
    n_simulations: number;
    /**
     * Spread of per-load BS&W around the lease average.
     * relative (default): value is % of the mean — e.g. 20 → σ = 0.20 × avg_bsw.
     * absolute: value is raw percentage points — e.g. 2.0 → σ = 2.0 %.
     */
    bsw_std_dev: number;
    bsw_std_mode: 'relative' | 'absolute';
}

// Results returned by backend — canonical definitions live in tableDefinitions.tsx
export type { RFPLeaseResult as LeaseResult, RFPRegionalSummaryItem as RegionalSummaryItem, RFPAnalysisResult } from '../../../contexts/tableDefinitions';

export interface RFPAnalysisPayload {
    leases: LeaseRow[];
    primary_destination: RFPConfig['primary_destination'];
    secondary_destination?: RFPConfig['secondary_destination'];
    waste_destination?: RFPConfig['waste_destination'];
    bsw_threshold: number;
    carrier: string;
    pricing_strategy: PricingStrategyConfig;
    month: number;
    year: number;
    monte_carlo?: MonteCarloConfig;
}
